import { type RpcName, type RpcParams, type RpcResult, rpcContracts } from "api-contracts";
import type { IRpcResponse } from "api-contracts/rpc";
import { safeParse } from "valibot";

import { appContext } from "../appContext";

export async function rpc<T extends RpcName>(rpcName: T, params: RpcParams<T>): Promise<RpcResult<T>> {
  // Validate params against the contract schema
  const contract = rpcContracts[rpcName];

  if (!contract) {
    throw new Error(`Unknown RPC: ${rpcName}`);
  }

  const validation = safeParse(contract.paramsSchema, params);

  if (!validation.success) {
    throw new Error(`Invalid parameters: ${validation.issues.map((issue) => issue.message).join(", ")}`);
  }

  try {
    const response = await fetch(`/api/rpc/${rpcName}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(appContext.currentAccessToken && {
          Authorization: `Bearer ${appContext.currentAccessToken}`,
        }),
      },
      body: JSON.stringify(validation.output),
    });

    const data = (await appContext.processRpcResponse(response)) as IRpcResponse<RpcResult<T>>;

    if (!data.success) {
      throw new Error(data.errorMessage || "RPC call failed");
    }

    const resultValidation = safeParse(contract.resultSchema, data.data);
    if (!resultValidation.success) {
      throw new Error(`Invalid response data: ${resultValidation.issues.map((issue) => issue.message).join(", ")}`);
    }

    return data.data!;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Network error occurred");
  }
}
